import { z, ZodTypeAny } from 'zod'
import { withMeta } from './zodType'

/**
 * Tabela de operações disponíveis por tipo (cópia do original).
 */
export const ExpressionOperationAvailable = {
  number: [
    { label: 'é igual a', value: { key: '=', kind: 'operator' } },
    { label: 'é diferente de', value: { key: '!=', kind: 'operator' } },
    { label: 'é menor ou igual a', value: { key: '<=', kind: 'operator' } },
    { label: 'é maior que', value: { key: '>', kind: 'operator' } },
    { label: 'é maior ou igual a', value: { key: '>=', kind: 'operator' } },
    { label: 'é menor que', value: { key: '<', kind: 'operator' } },
    { label: 'está entre', value: { key: 'BETWEEN', kind: 'operator' } },
    { label: 'não está entre', value: { key: 'NOT BETWEEN', kind: 'operator' } },
  ],
  string: [
    { label: 'é igual a', value: { key: '=', kind: 'operator' } },
    { label: 'é diferente de', value: { key: '!=', kind: 'operator' } },
  ],
  enumerated: [
    { label: 'é igual a', value: { key: '=', kind: 'operator' } },
    { label: 'é diferente de', value: { key: '!=', kind: 'operator' } },
  ],
  boolean: [{ label: 'é', value: { key: '=', kind: 'operator' } }],
}

/**
 * Operadores binários possíveis em Expression.
 */
export type ExpressionBinaryOperator =
  | '='
  | '!='
  | '<>'
  | '>'
  | '>='
  | '<'
  | '<='
  | 'LIKE'
  | 'NOT LIKE'
  | 'ILIKE'
  | 'NOT ILIKE'
  | 'IN'
  | 'NOT IN'
  | 'IS'
  | 'IS NOT'
  | 'BETWEEN'
  | 'NOT BETWEEN'
  | 'AND'
  | 'OR'
  | '+'
  | '-'
  | '*'
  | '/'
  | '%'
  | '^'
  | '||'

/**
 * Operadores unários possíveis em Expression.
 */
export type ExpressionUnaryOperator = '-' | '+' | 'NOT'

/**
 * Funções possíveis na Expression (inclui algumas de geometria).
 */
export type ExpressionFunction =
  | 'cast'
  | 'count'
  | 'sum'
  | 'avg'
  | 'max'
  | 'min'
  | 'upper'
  | 'lower'
  | 'length'
  | 'substring'
  | 'coalesce'
  | 'floor'
  | 'ceiling'
  | 'round'
  | 'now'
  | 'greatest'
  | 'least'
  | 'exists'
  // Geometry
  | 'setSrid'
  | 'centroid'
  | 'multi'
  | 'geomFromGeoJSON'
  | 'point'
  | 'dump'
  | 'area'
  | 'collect'
  | 'simplifyPreserveTopology'
  | 'makeValid'
  | 'intersects'
  | 'intersection'
  | 'getFaceGeometry'
  | 'geomFromEwkt'
  | 'contains'
  | 'isEmpty'
  | 'asGeoJSON'
  | 'pointOnSurface'
  | 'lineMerge'
  | 'locateAlong'
  | 'addMeasure'
  | 'distance'
  | 'dwithin'
  | 'difference'
  | 'buffer'
  | 'azimuth'

/**
 * Representa um "shape" de objeto:
 *   - Chaves: nomes de propriedades
 *   - Valores: schemas Zod
 */
export type ShapeBase = Record<string, z.ZodTypeAny>

/**
 * Estrutura recursiva de Expression, igual ao seu código original.
 */
export type Expression<Property extends string | number | symbol = string> =
  | {
      type: 'expression'
      kind: 'binary'
      operator: ExpressionBinaryOperator
      left: Expression<Property>
      right: Expression<Property>
    }
  | {
      type: 'expression'
      kind: 'binary'
      operator: ExpressionBinaryOperator
      left: Expression<Property>
      right: {
        type: 'expression'
        kind: 'binary'
        operator: ExpressionBinaryOperator
        left: Expression<Property>
        right: Expression<Property>
      }
    }
  | {
      type: 'expression'
      kind: 'ternary'
      condition: {
        type: 'expression'
        kind: 'binary'
        operator: ExpressionBinaryOperator
        left: Expression<Property>
        right: Expression<Property>
      }
      whenTrue: Expression<Property>
      whenFalse: Expression<Property>
    }
  | {
      type: 'expression'
      kind: 'unary'
      operator: ExpressionUnaryOperator
      operand: Expression<Property>
    }
  | {
      type: 'expression'
      kind: 'summation'
      arguments: Expression<Property>[]
    }
  | {
      type: 'expression'
      kind: 'function'
      name: ExpressionFunction
      arguments: Expression<Property>[]
    }
  | {
      type: 'expression'
      kind: 'property'
      property: Property
      literalType?: 'string' | 'number' | 'boolean'
      subType?: unknown
    }
  | {
      type: 'expression'
      kind: 'literal'
      value: string | number | boolean
      literalType: 'string' | 'number' | 'boolean'
      subType?: unknown
    }
  | {
      type: 'expression'
      kind: 'null'
    }
  | {
      type: 'expression'
      kind: 'list'
      items: Expression<Property>[]
    }

/**
 * Interface "antiga" para compatibilidade. Agora, não depende mais de createType()
 * ou AbstractType, mas mantém a "assinatura" do seu código original.
 *
 * @template ExpressionGeneric O tipo de expressão
 * @template Shape O shape-base
 */
export interface ExpressionType<
  ExpressionGeneric extends Expression<any> | null = Expression,
  Shape extends ShapeBase = ShapeBase,
> {
  readonly type: 'expression'
  readonly shape: Shape
  readonly currentValue?: ExpressionGeneric

  // isRequired(): ExpressionType<Exclude<ExpressionGeneric, null>, Shape>
  // isOptional(): ExpressionType<ExpressionGeneric | null, Shape>
}

// Tipo genérico para a expressão Zod com metadados
export type ZodExpressionType<T extends ZodTypeAny> = z.ZodType<
  z.output<T>,
  T['_def'],
  z.input<T>
> & {
  currentMeta?: {
    label?: string
  }
}

/**
 * Função auxiliar que cria o schema recursivo de Expression,
 * usando um z.enum(...) com as chaves do shape para "property".
 */
function createExpressionSchema<Property extends string>(
  propertySchema: z.ZodEnum<[Property, ...Property[]]>,
): z.ZodType<Expression<Property>> {
  // Precisamos de sub-schemas para operatores e funções (versão 'z.enum')
  const expressionBinaryOperator = z.enum([
    '=',
    '!=',
    '<>',
    '>',
    '>=',
    '<',
    '<=',
    'LIKE',
    'NOT LIKE',
    'ILIKE',
    'NOT ILIKE',
    'IN',
    'NOT IN',
    'IS',
    'IS NOT',
    'BETWEEN',
    'NOT BETWEEN',
    'AND',
    'OR',
    '+',
    '-',
    '*',
    '/',
    '%',
    '^',
    '||',
  ] as const)

  const expressionUnaryOperator = z.enum(['-', '+', 'NOT'] as const)

  const expressionFunction = z.enum([
    'cast',
    'count',
    'sum',
    'avg',
    'max',
    'min',
    'upper',
    'lower',
    'length',
    'substring',
    'coalesce',
    'floor',
    'ceiling',
    'round',
    'now',
    'greatest',
    'least',
    'exists',
    // geometry
    'setSrid',
    'centroid',
    'multi',
    'geomFromGeoJSON',
    'point',
    'dump',
    'area',
    'collect',
    'simplifyPreserveTopology',
    'makeValid',
    'intersects',
    'intersection',
    'getFaceGeometry',
    'geomFromEwkt',
    'contains',
    'isEmpty',
    'asGeoJSON',
    'pointOnSurface',
    'lineMerge',
    'locateAlong',
    'addMeasure',
    'distance',
    'dwithin',
    'difference',
    'buffer',
    'azimuth',
  ] as const)

  // A recursão em si (z.lazy)
  const schema: z.ZodType<Expression<Property>> = z.lazy(() =>
    z.union([
      // 1) Binary
      z.object({
        type: z.literal('expression'),
        kind: z.literal('binary'),
        operator: expressionBinaryOperator,
        left: schema,
        right: schema,
      }),
      // 2) Binary (right é outro binary fixo)
      z.object({
        type: z.literal('expression'),
        kind: z.literal('binary'),
        operator: expressionBinaryOperator,
        left: schema,
        right: z.object({
          type: z.literal('expression'),
          kind: z.literal('binary'),
          operator: expressionBinaryOperator,
          left: schema,
          right: schema,
        }),
      }),
      // 3) Ternary
      z.object({
        type: z.literal('expression'),
        kind: z.literal('ternary'),
        condition: z.object({
          type: z.literal('expression'),
          kind: z.literal('binary'),
          operator: expressionBinaryOperator,
          left: schema,
          right: schema,
        }),
        whenTrue: schema,
        whenFalse: schema,
      }),
      // 4) Unary
      z.object({
        type: z.literal('expression'),
        kind: z.literal('unary'),
        operator: expressionUnaryOperator,
        operand: schema,
      }),
      // 5) Summation
      z.object({
        type: z.literal('expression'),
        kind: z.literal('summation'),
        arguments: z.array(schema),
      }),
      // 6) Function
      z.object({
        type: z.literal('expression'),
        kind: z.literal('function'),
        name: expressionFunction,
        arguments: z.array(schema),
      }),
      // 7) Property
      z.object({
        type: z.literal('expression'),
        kind: z.literal('property'),
        property: propertySchema,
        literalType: z.enum(['string', 'number', 'boolean']).optional(),
        subType: z.unknown().optional(),
      }),
      // 8) Literal
      z.object({
        type: z.literal('expression'),
        kind: z.literal('literal'),
        value: z.union([z.string(), z.number(), z.boolean()]),
        literalType: z.enum(['string', 'number', 'boolean']).optional(),
        subType: z.unknown().optional(),
      }),
      // 9) Null
      z.object({
        type: z.literal('expression'),
        kind: z.literal('null'),
      }),
      // 10) List
      z.object({
        type: z.literal('expression'),
        kind: z.literal('list'),
        items: z.array(schema),
      }),
    ]),
  ) as z.ZodType<Expression<Property>>

  return schema
}

/**
 * Função principal (default) que substitui:
 *
 * export default function expression<Shape extends ShapeBase = ShapeBase>(shape: Shape): ExpressionType<Expression<keyof Shape>...>
 *
 * Agora, ela recebe um shape e gera um schema de Expression,
 * onde `property` é uma das chaves do shape.
 */
export default function expression<Shape extends ShapeBase>(shape: Shape) {
  // 1) Extrair as chaves do shape
  const keys = Object.keys(shape) as (keyof Shape & string)[]

  if (keys.length < 1) {
    console.warn('Warning: received an empty shape in expression(). property enum will be empty.')
  }

  // 2) Cria um enum com as chaves (para `property`)
  const propertySchema = z.enum(keys as [keyof Shape & string, ...(keyof Shape & string)[]])

  // 3) Cria o schema recursivo
  const baseExpressionSchema = createExpressionSchema<keyof Shape & string>(propertySchema)

  // 4) Embrulha no withMeta, se quiser usar .meta()
  const extendedSchema = withMeta(baseExpressionSchema)

  // 5) Unimos o Zod schema com esse placeholder para manter API
  return extendedSchema.meta({ shape })
}
